import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useNavigate, useLocation, useParams } from 'react-router';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Dialog, DialogActions, DialogContent, Button as ButtonMUI, IconButton } from '@mui/material';
import { makeStyles } from '@mui/styles';
import InputText from '@/components/UI/InputText/InputText';
import { ENV, env, getAbsoluteRoute, ROUTES, stateTabs, tabsCRSearchOrdered } from '@/helpers/constants';
import { deepClone, getVersionUrlParam } from '@/helpers/utils';
import { checkIfTermNotAvailableInLanguaje } from '@/hooks/useCampaignSearchSelected';
import useToastContext from '@/hooks/useToastContext';
import { setCampaignSearch } from '@/redux/Campaign/campaign.action';
import { getCampaignInfo, getCampaignSearch } from '@/redux/Campaign/campaign.reducer';
import { getHeaderLanguage } from '@/redux/Header/header.reducer';
import { CampaignSearch } from '@/types';
import styles from './CampaignSearchPopup.module.scss';

const getSelectIndexArray = (actualIndex, totalLength, isLeft) => {
  if (isLeft && actualIndex === 0) return (totalLength - 1);
  if (!isLeft && actualIndex === (totalLength - 1)) return 0;

  return isLeft ? (actualIndex - 1) : (actualIndex + 1);
};

const useStyles = makeStyles({
  dialogPaperTop: {
    position: 'absolute',
    top: '0px',
    marginTop: '5px',
  },
  dialogPaperSeeHeader: {
    position: 'absolute',
    top: '15%',
  },
  dialogAction: {
    padding: '0px',
  },
  dialogContent: {
    paddingBottom: '0px',
  },
});

type CampaignSearchPopupProps = {
  campaignSearch: CampaignSearch;
  setCRSearch: (campaignSearch: CampaignSearch) => void;
  headerLanguage: string;
  isSubCR?: boolean;
};

const CampaignSearchPopup: React.FC<CampaignSearchPopupProps> = (props) => {
  const { campaignSearch, setCRSearch, headerLanguage, isSubCR = false } = props;
  const { t } = useTranslation();
  const { id, version } = useParams<{ id?: string; version?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const classes = useStyles();
  const [indicateHeader, setIndicateHeader] = useState(false);
  const checkArrowsDisabled = campaignSearch.resultCounter < 2;
  const { addToast } = useToastContext();
  const subCrBase = getAbsoluteRoute(ROUTES.SUB_CR_DETAIL);
  const crBase = getAbsoluteRoute(ROUTES.CR_DETAIL);

  const close = (ev?: React.MouseEvent<HTMLButtonElement, MouseEvent>, reason: string = '') => {
    if (reason === 'backdropClick') return;
    setCRSearch({
      ...campaignSearch,
      active: false,
    });

    if (isSubCR) {
      navigate({
        search: location.search.replace(/(\?search=.*$|&search=.*$)/, ''),
      });
    } else {
      navigate({
        pathname: location.pathname,
        search: location.search,
      });
    }
  };

  const moveSearchPointer = (isLeft = false) => {
    const newCampaignSearch = deepClone(campaignSearch);
    const indexSelected = campaignSearch.results?.findIndex((field) => field.selected);
    const indexToSelect = getSelectIndexArray(indexSelected, newCampaignSearch.results.length, isLeft);

    newCampaignSearch.results = newCampaignSearch.results
      .map((field, index) => ({ ...field, selected: index === indexToSelect, sameTabSelected: false }));
    const fieldSelected = newCampaignSearch.results?.find((field) => field.selected);
    newCampaignSearch.fieldSelected = fieldSelected;
    newCampaignSearch.results = newCampaignSearch.results
      .map((field) => ({ ...field, sameTabSelected: field?.tab === fieldSelected.tab }));

    setCRSearch(newCampaignSearch);
    setIndicateHeader(newCampaignSearch?.fieldSelected?.tab === 'campaign');

    const targetKey = newCampaignSearch?.fieldSelected?.tab ?? (isSubCR ? stateTabs.feedback : stateTabs.description);
    const targetPath = tabsCRSearchOrdered()[targetKey];

    if (!targetPath) {
      return;
    }

    navigate({
      pathname: isSubCR
        ? `${subCrBase}/${id}/${targetPath}`
        : `${crBase}/${id}${getVersionUrlParam(version)}/${targetPath}`,
      search: location.search,
    });
  };

  useEffect(() => {
    if (headerLanguage && checkIfTermNotAvailableInLanguaje(campaignSearch.fieldSelected?.field, headerLanguage)) {
      addToast(t('alert.termNotAvailableInLang'), 'Warning');
    }
  }, [campaignSearch.fieldSelected]);// eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setIndicateHeader(campaignSearch.fieldSelected?.tab === 'campaign');

    if (campaignSearch.resultCounter === 0) {
      addToast(t('alert.termNotShowed'), 'Warning');
    }
  }, []);

  return (
    <Dialog
      open
      maxWidth="md"
      fullWidth
      onClose={(ev: React.MouseEvent<HTMLButtonElement, MouseEvent>, reason?: string) => close(ev, reason)}
      classes={{ paper: !indicateHeader ? classes.dialogPaperTop : classes.dialogPaperSeeHeader }}
      BackdropProps={{ style: { backgroundColor: 'rgb(0 0 0 / 7%)' } }}
    >
      <DialogContent classes={{ root: classes.dialogContent }}>
        <div className={styles.PopupSearchCR}>
          <InputText
            id="input-free-text-popup"
            value={campaignSearch?.searchedText}
            label={t('search')}
            handleChange={() => console.log('soon??')}
            formControlClass={styles.FreeTextSearch}
            icoSearch={`${t(campaignSearch.resultCounter > 1 ? 'xResults' : 'xResult', { x: campaignSearch.resultCounter })}`}
            isCampaignSearchPopUpInput
          />
          <div className={styles.Arrows}>
            <IconButton
              aria-label="before"
              id="button-cr-search-before"
              data-testid="button-cr-search-before"
              onClick={() => moveSearchPointer(true)}
              disabled={checkArrowsDisabled}
              size="large"
            >
              <NavigateBeforeIcon fontSize="large" />
            </IconButton>
            <IconButton
              aria-label="next"
              id="button-cr-search-next"
              data-testid="button-cr-search-after"
              onClick={() => moveSearchPointer()}
              disabled={checkArrowsDisabled}
              size="large"
            >
              <NavigateNextIcon fontSize="large" />
            </IconButton>
          </div>
        </div>
      </DialogContent>
      <DialogActions classes={{ root: classes.dialogAction }}>
        {[ENV.DEV, ENV.LOCAL].includes(env) && (
          <>TAB ACTUAL: {campaignSearch?.fieldSelected?.tab} | field actual: {campaignSearch?.fieldSelected?.field.value}</>
        )}
        <ButtonMUI
          className={styles.CloseButton}
          id="button-close-cr-search-text"
          data-testid="button-close-cr-search-text"
          color="primary"
          onClick={() => close()}// event is not used
        >
          {t('dialog.close')}
        </ButtonMUI>
      </DialogActions>
    </Dialog>
  );
};

const mapStateToProps = (state) => ({
  campaignSearch: getCampaignSearch(state),
  headerLanguage: getHeaderLanguage(state) || getCampaignInfo(state)?.defaultLanguage,
});

const actionCreators = {
  setCRSearch: setCampaignSearch,
};

export default connect(mapStateToProps, actionCreators)(CampaignSearchPopup);
